import { z } from 'zod';

/**
 * Checkout settings staff control under Admin > Checkout: whether cash on delivery is offered, the "pay online" discount, how long an unpaid online order is
 * held, and when a customer who left checkout is reminded. All money is in paise.
 */

export const onlineDiscountSchema = z.object({
  enabled: z.boolean(),
  /** a flat amount off when the customer pays online instead of cash on delivery */
  amount: z.number().int().min(0).max(1_000_000),
  /** only for orders of at least this much (after any coupon); 0 = every order */
  minSubtotal: z.number().int().min(0).max(100_000_000),
});
export type OnlineDiscountSettings = z.infer<typeof onlineDiscountSchema>;

export const codSchema = z.object({
  /** let customers choose cash on delivery; when off, only online payment is offered and COD orders are refused */
  enabled: z.boolean(),
});
export type CodSettings = z.infer<typeof codSchema>;

export const recoverySchema = z.object({
  enabled: z.boolean(),
  /** how long after the customer was last active before the first reminder */
  afterMinutes: z.number().int().min(5).max(10_080),
  /** how many reminders to send, at most */
  maxReminders: z.number().int().min(1).max(3),
  /** hours between reminders */
  gapHours: z.number().int().min(1).max(168),
});
export type RecoverySettings = z.infer<typeof recoverySchema>;

export const checkoutSettingsSchema = z
  .object({
    onlineDiscount: onlineDiscountSchema,
    cod: codSchema,
    /** how long an online order waits for its payment before it is cancelled and its stock freed */
    paymentHoldMinutes: z.number().int().min(15).max(1440),
    recovery: recoverySchema,
  })
  .superRefine((v, ctx) => {
    if (v.onlineDiscount.enabled && v.onlineDiscount.amount <= 0) {
      ctx.addIssue({ code: 'custom', path: ['onlineDiscount', 'amount'], message: 'Enter how much to take off, or turn the discount off' });
    }
  });
export type CheckoutSettings = z.infer<typeof checkoutSettingsSchema>;

export const DEFAULT_CHECKOUT_SETTINGS: CheckoutSettings = {
  onlineDiscount: { enabled: true, amount: 5000, minSubtotal: 0 },
  cod: { enabled: true },
  paymentHoldMinutes: 60,
  recovery: { enabled: true, afterMinutes: 30, maxReminders: 2, gapHours: 24 },
};

/**
 * What paying online takes off an order whose goods cost `goodsTotal` (after any coupon). Never more than the goods cost,
 * and nothing when the offer is off or the order is below the minimum.
 */
export function onlineDiscountFor(offer: OnlineDiscountSettings, goodsTotal: number): number {
  if (!offer.enabled || offer.amount <= 0 || goodsTotal <= 0) return 0;
  if (goodsTotal < offer.minSubtotal) return 0;
  return Math.min(offer.amount, goodsTotal);
}

// ---------------------------------------------------------------- checkouts in progress --

export const CheckoutDraftStatus = {
  OPEN: 'OPEN',
  PAYMENT_PENDING: 'PAYMENT_PENDING',
  PAYMENT_CANCELLED: 'PAYMENT_CANCELLED',
  ORDERED: 'ORDERED',
} as const;
export type CheckoutDraftStatus = (typeof CheckoutDraftStatus)[keyof typeof CheckoutDraftStatus];

export const CHECKOUT_DRAFT_STATUS_LABEL: Record<CheckoutDraftStatus, string> = {
  OPEN: 'Left at checkout',
  PAYMENT_PENDING: 'Waiting for payment',
  PAYMENT_CANCELLED: 'Payment cancelled',
  ORDERED: 'Ordered',
};

const trimmed = (max: number) => z.string().trim().max(max);

/** What the checkout page saves as the customer fills it in. Every field is optional: they may not have got that far. */
export const checkoutDraftSchema = z.object({
  cartId: z.string().uuid(),
  email: z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), z.string().email().max(254).optional()),
  shippingAddress: z
    .object({
      fullName: trimmed(120),
      phone: trimmed(20),
      line1: trimmed(200),
      line2: trimmed(200),
      city: trimmed(80),
      state: trimmed(80),
      postalCode: trimmed(12),
      country: trimmed(2),
    })
    .partial()
    .optional(),
  paymentMethod: z.enum(['online', 'cod']).optional(),
});
export type CheckoutDraftInput = z.infer<typeof checkoutDraftSchema>;

/** The customer tells us the payment window was closed or the payment failed. */
export const paymentAttemptSchema = z.object({
  orderId: z.string().min(1),
  outcome: z.enum(['dismissed', 'failed']),
  reason: z.string().trim().max(300).optional(),
  code: z.string().trim().max(80).optional(),
});
export type PaymentAttemptInput = z.infer<typeof paymentAttemptSchema>;

export const checkoutDraftParamSchema = z.object({ id: z.string().uuid() });
export const checkoutDraftQuerySchema = z.object({
  status: z.enum(['OPEN', 'PAYMENT_PENDING', 'PAYMENT_CANCELLED', 'ORDERED', 'ABANDONED']).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export interface CheckoutDraftDTO {
  id: string;
  status: CheckoutDraftStatus;
  customerName: string | null;
  phone: string | null;
  email: string | null;
  itemCount: number;
  /** the goods total when the customer was last at checkout, in minor units */
  total: number;
  currency: string;
  items: { name: string; quantity: number }[];
  orderNumber: string | null;
  cancelReason: string | null;
  reminderCount: number;
  lastReminderAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  /** a reminder has not gone out yet and is allowed, so "Remind" will send one */
  canRemind: boolean;
}

export interface CheckoutDraftListDTO {
  drafts: CheckoutDraftDTO[];
  /** how many are waiting right now, by status, to show on the filter chips */
  counts: Record<CheckoutDraftStatus, number>;
}

export interface AdminCheckoutDTO {
  settings: CheckoutSettings;
  /** what the store has set up on the server for taking payments, so staff can see why a method is missing */
  payments: {
    provider: string;
    /** false when the server's COD_ENABLED is off: cash on delivery is then unavailable whatever the setting below says */
    codEnabled: boolean;
    /** whether customers can pay online right now (Razorpay set up, or the test provider) */
    onlineAvailable: boolean;
    razorpay: {
      configured: boolean;
      /** "test" or "live", from the key id */
      mode: 'test' | 'live' | null;
      webhookSecretSet: boolean;
      webhookUrl: string;
      events: string[];
      /** the last webhook Razorpay sent us, so staff can tell the connection works */
      lastWebhookAt: string | null;
      lastWebhookType: string | null;
    };
  };
}
